import { FC } from "react";
import Container from "react-bootstrap/Container";
import Alert from "react-bootstrap/Alert";
import Table from "react-bootstrap/Table";
import { Navigate } from "react-router-dom";
import { useRouteParams } from "app/route/route";
import { deleteTestTypePrerequisiteRoute, testTypePrerequisitesRoute } from "app/route/Routes";
import { NavigationScreen } from "app/component/screen/NavigationScreen";
import { RouteBreadcrumbs } from "app/route/RouteBreadcrumbs";
import { useDeleteAllVersionsOfTestTypePrerequisiteMutation, useTestTypePrerequisiteQuery } from "app/api/graph/types";
import { testTypePrerequisiteGraphId } from "app/api/graph/helpers";
import { LoadingScreen } from "app/component/screen/LoadingScreen";
import { ErrorScreen } from "app/component/screen/ErrorScreen";
import { ConfirmDelete } from "app/component/form/ConfirmDelete";

export const DeleteTestTypePrerequisiteScreen: FC = () => {
    const { testTypePrerequisiteId } = useRouteParams(deleteTestTypePrerequisiteRoute);
    const { data, loading, error } = useTestTypePrerequisiteQuery({
        variables: { testTypePrerequisiteId: testTypePrerequisiteGraphId(testTypePrerequisiteId) },
    });
    const [deleteTestTypePrerequisite, { data: deleteData, loading: deleting, error: deleteError }] =
        useDeleteAllVersionsOfTestTypePrerequisiteMutation({
            variables: { input: { id: testTypePrerequisiteGraphId(testTypePrerequisiteId) } },
        });
    const testTypePrerequisite = data?.testTypePrerequisite;

    if (loading) {
        return <LoadingScreen />;
    }
    if (error) {
        return <ErrorScreen title="Error" message={error.message} />;
    }
    if (!testTypePrerequisite) {
        return <ErrorScreen title="Error" message="Not found." />;
    }
    if (deleteData?.deleteAllVersionsOfTestTypePrerequisite) {
        return <Navigate to={testTypePrerequisitesRoute.build({})} />;
    }

    return (
        <NavigationScreen>
            <Container>
                <RouteBreadcrumbs route={deleteTestTypePrerequisiteRoute} />
                <h1>Delete prerequisite</h1>
                {deleteError ? <Alert variant="danger">{deleteError.message}</Alert> : null}
                <p>Are you sure that you want to permanently delete this prerequisite?</p>
                <Table responsive="sm">
                    <tbody>
                        <tr>
                            <th>Identifier</th>
                            <td>{testTypePrerequisite.identifier ?? "-"}</td>
                        </tr>
                        <tr>
                            <th>Description</th>
                            <td>{testTypePrerequisite.description ?? "-"}</td>
                        </tr>
                    </tbody>
                </Table>
                <Alert variant="danger">
                    <p>
                        This will <strong>permanently</strong> delete all versions of this prerequisite - which cannot
                        be reversed.
                    </p>
                    Note: report prerequisites that are based on this master prerequisite will be unlinked, but not
                    deleted - they must be deleted separately if needed.
                </Alert>
                <ConfirmDelete onConfirm={() => deleteTestTypePrerequisite()} deleting={deleting} />
            </Container>
        </NavigationScreen>
    );
};
